import {
  type FocusEvent,
  type HTMLAttributes,
  type KeyboardEvent,
  type RefCallback,
  type RefObject,
  useCallback,
  useMemo,
} from 'react'

import {useBoardFocusGrid, useBoardFocusStore, useIsBoardPositionRendered} from './focus/contexts'
import {boardFocusReducer, isSamePosition} from './focus/focus-reducer'
import {boardFocusMoveForKey, isAcrossTheBoard, isOwnKeyboardTarget} from './focus/keys'
import {useBoardConfig, useBoardElementRef, useBoardInteraction} from './provider/contexts'
import {canScrollFurther} from './scroll/can-scroll-further'
import {isTargetClipped} from './scroll/is-target-clipped'
import {useResetScroll} from './scroll/reset-scroll'
import {HORIZONTAL_DEAD_ZONE_RATIO, ROOT_VERTICAL_BAND} from './scroll/settings'
import {useAutoScroll} from './scroll/use-auto-scroll'
import {useHasUnboundedHeight} from './scroll/use-has-unbounded-height'
import {type ScrollbarSizes, useMeasureScrollbars} from './scroll/use-measure-scrollbars'
import {useElementRef} from './use-element-ref'

export type UseBoardRootResult = {
  rootProps: HTMLAttributes<HTMLElement> & {ref: RefCallback<HTMLElement>}
  /**
   * The board's horizontal scroll container, which is the root itself.
   *
   * Returned as well as spread, because the parts that come later have to move it
   * without owning it: bringing a column into view, and scrolling a card into range
   * once it mounts.
   */
  scrollRef: RefObject<HTMLElement | null>
  /**
   * How much of the root's box its scrollbars take up. Zero where the platform overlays
   * them. The board keeps its own layout clear of them, and a caller assembling a board
   * from the hooks can do the same.
   */
  scrollbarSizes: ScrollbarSizes
  /**
   * The board is taller than the window, so the page is scrolling instead of the board.
   *
   * Always a mistake in the page around it, and one that is otherwise silent: the
   * scrollbars, the auto-scroll and the paging sentinels all stop working together, with
   * nothing on screen to say so. Usually it means the board was given no height at all,
   * because `block-size: 100%` of a parent with no height resolves to the board's own
   * contents. Report it when this is true. `@github-ui/board` writes to the console
   * outside production, where the only person who can fix it is the one looking, and
   * draws nothing: the message names a mistake a reader did not make and cannot act on.
   */
  hasUnboundedHeight: boolean
  /**
   * Whether the caller said its rows are named, read back from the provider's
   * `hasRowHeaders`.
   *
   * The same fact `useBoardCell` names a cell with, so a root styled around the presence
   * of row headers and a cell named after one cannot disagree. Read this rather than
   * asking whether a row header was rendered: that derives the fact from the drawing of
   * it, and the two are stated in different places.
   */
  hasRowHeaders: boolean
}

/**
 * The board's outermost element: the thing that is named, and the thing that scrolls
 * sideways.
 *
 * **The board must be given a bounded height.** It owns this scroll container, so a page
 * that lets it grow gets a board that stretches the document instead of scrolling, and
 * everything that measures against a scroll viewport is wrong from that point on.
 *
 * No `role` is returned. A board is significant enough to belong in a page's table of
 * contents, and `group` is the one grouping role ARIA excludes from it, so the caller
 * spreads these onto a sectioning element (`<section>`), which with an accessible name
 * maps to `region` natively. That is the repository's preference for a native element
 * over an explicit role.
 *
 * `tabIndex` is here because the root is a scroll container, and a scroll container
 * that cannot be reached by keyboard cannot be scrolled by keyboard. It is `-1` while
 * something inside the board is drawing the roving item, which is the usual case: a
 * composite widget is one tab stop, and that stop should be the card or cell the reader
 * left off at rather than the container around it. It goes back to `0` whenever nothing
 * inside claims the position, which is a board with no columns or no rows, and a board
 * whose window has scrolled past the column the position names. A board with no tab stop
 * at all is a board a keyboard cannot reach.
 *
 * `onKeyDown` moves that position with the arrow keys, `Home` and `End`, and with the two
 * modifiers `BoardFocusModifiers` names: `Alt` and a vertical arrow jumps between
 * swimlanes, and `Control` with `Home` or `End` reaches the ends of the row. The three
 * paging ghosts and the row headers are positions on those same axes, so they are reached
 * by the same keys.
 *
 * `onFocus` is how the board notices focus landing inside it on something that is not a
 * position at all, which is where a control you draw in a header or in the band of a cell
 * sits. Nothing else can see that: no position reports arriving, because none of them was
 * focused, and focus has not left the board for `onBlur` to see either. The keys are left
 * alone there for the same reason they are left alone for a scroll container with room
 * left.
 */
export function useBoardRoot(): UseBoardRootResult {
  const {labels, resetScrollKey, hasRowHeaders, autoScrollRate} = useBoardConfig()
  const {draggingCards} = useBoardInteraction()
  const isPositionRendered = useIsBoardPositionRendered()
  const {dispatch, getState, isFocusAtPosition} = useBoardFocusStore()
  const grid = useBoardFocusGrid()
  const {ref: scrollRef, setRef: setScrollRef} = useElementRef()
  const boardElementRef = useBoardElementRef()

  // The same element twice: the board reads it back to measure and scroll, and the
  // provider publishes it so a paging sentinel can ask whether a movement happened inside
  // this board rather than somewhere else on the page.
  const setRef = useCallback<RefCallback<HTMLElement>>(
    element => {
      setScrollRef(element)
      boardElementRef.current = element
    },
    [boardElementRef, setScrollRef],
  )

  const scrollbarSizes = useMeasureScrollbars(scrollRef)

  // A scrollbar sits at the end edge of its axis and is not scrollable content, so the
  // buffers are measured against where the content ends rather than where the box does.
  // Without this, a board with a scrollbar never quite reaches full scroll speed, and
  // the pointer has to hover the scrollbar itself to get there.
  const offsetX = useMemo<readonly [number, number]>(() => [0, scrollbarSizes.vertical], [scrollbarSizes.vertical])
  const offsetY = useMemo<readonly [number, number]>(() => [0, scrollbarSizes.horizontal], [scrollbarSizes.horizontal])

  useAutoScroll({
    active: draggingCards.length > 0,
    scrollRef,
    // Locked sideways, and measured against the whole width: going across the board is
    // what most of the board's own width is for.
    axis: 'x',
    strength: autoScrollRate,
    deadZoneRatioX: HORIZONTAL_DEAD_ZONE_RATIO,
    offsetX,
    offsetY,
  })

  /*
    The same container, downwards, and a second call rather than one unlocked one because
    the two axes are asking different questions and want different bands. Each instance
    writes only the offset its lock allows, so neither can fight the other over the same
    container, and each keeps the containment rule that goes with its own lock.

    A card aimed below a cell is usually being aimed further down that cell's own list,
    which is why this band is only the last stretch of the board rather than half of it:
    inside the board a cell scrolls itself, and at the very edge of the board the reader
    is asking to go to a swimlane they cannot see. Without this a card could never be
    dragged to a swimlane that is off screen at all, because a cell only ever scrolls its
    own cards and nothing else moved downwards.
  */
  useAutoScroll({
    active: draggingCards.length > 0,
    scrollRef,
    axis: 'y',
    strength: autoScrollRate,
    bufferY: ROOT_VERTICAL_BAND,
    offsetX,
    offsetY,
  })

  useResetScroll(scrollRef, resetScrollKey)

  const hasUnboundedHeight = useHasUnboundedHeight(scrollRef)

  // On the root rather than on each roving item, because the event bubbles here anyway
  // and one handler is one handler however many cards are on screen.
  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      /*
        Read here rather than through `@github-ui/ui-commands`, for two reasons. Arrow
        keys inside a roving tabindex are the widget's own behaviour rather than an
        application command anybody would rebind, and a packaging test pins this package's
        dependencies, which that library is not among.
      */
      // `Shift` is how a range gets selected, which `useBoardCard` claims on a click, and
      // `Meta` is window management on Windows and a document gesture on macOS. Neither is
      // the board's on any key. `Alt` and `Control` each are, on the keys named in
      // `BoardFocusModifiers`, and the map leaves them alone on every other key.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.defaultPrevented || event.metaKey || event.shiftKey) return
      // AltGr reports both, and is how a reader types a character rather than either of
      // the board's two modifiers.
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.altKey && event.ctrlKey) return
      if (isOwnKeyboardTarget(event.target)) return

      // Read rather than subscribed to. The root answers a key it is already handling, so
      // it has no reason to re-render every time focus moves.
      const focusState = getState()

      // Nowhere to move on a board with no columns or no rows.
      if (focusState.position === null) return

      // The position names a column or row the board has not drawn, so there is no element
      // to move focus onto and nothing a reader would see happen. Scrolling is what brings
      // that column back, so the key is better left to do it. Moving the window to the
      // position instead is https://github.com/github/views/issues/40.
      if (!isPositionRendered) return

      // Nobody is at the position: focus is on something the caller drew inside the board
      // that is not one of them, which is a button in a column header, one beside a
      // swimlane's name, or an "Add card" in the band of a cell. Only an element at a
      // position reports focus arriving, so this is the board asking whether one did. The
      // key belongs to whatever has it, or to scrolling, exactly as it does for a scroll
      // container with room left.
      //
      // Naming the tags in `isOwnKeyboardTarget` instead is the wrong fix: the same tags
      // inside a card must keep moving between cards, and a list of tags cannot tell the
      // two apart. The root is excluded because it is not a position either, and taking
      // its arrow keys is the whole point of a roving tabindex.
      if (event.target !== event.currentTarget && !focusState.hasFocus) return

      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const key = event.key
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      const modifiers = {alt: event.altKey, control: event.ctrlKey}

      const move = boardFocusMoveForKey(key, modifiers, isAcrossTheBoard(key) && isRightToLeft(event.currentTarget))
      if (move === null) return

      // Something inside the board is focused and can still be scrolled the way this key
      // points, so the key belongs to it. The root itself is excluded: it can nearly
      // always scroll, and taking its arrow keys is the whole point of a roving tabindex.
      if (
        event.target !== event.currentTarget &&
        canScrollFurther(
          event.target,
          move.axis === 'column' ? 'inline' : 'block',
          move.step === 'previous' || move.step === 'first' ? 'start' : 'end',
        )
      ) {
        return
      }

      // A card taller than the cell holding it, with the rest of it still to read. The
      // card is not a scroll container, so the guard above says nothing about it, and the
      // key is better left to scroll the cell than spent moving off a card the reader has
      // only seen the top of. Only on the axis they are reading along: the modifier jump
      // is how they leave such a card deliberately.
      //
      // The root is excluded here for the same reason it is excluded above: it is the
      // board's own scroll container, and a board given no bounded height is clipped by
      // the page itself, which would take its arrow keys away in exactly the state that
      // most needs them.
      if (
        event.target !== event.currentTarget &&
        move.axis === 'card' &&
        (move.step === 'previous' || move.step === 'next') &&
        isTargetClipped(event.target, move.step === 'previous' ? 'start' : 'end')
      ) {
        return
      }

      const action = {type: 'move', grid, axis: move.axis, step: move.step} as const

      // A move that lands where it started is a key this board has no use for, and taking
      // it would leave a reader pressing it against an edge with nothing happening at all.
      // Left alone it scrolls, which is how the paging ghost past the last column, the one
      // below the last swimlane, and the part of a cell below its last card are reached.
      if (isSamePosition(boardFocusReducer(focusState, action).position, focusState.position)) return

      // Otherwise the root scrolls as well as focus moving, which is the same gesture
      // being answered twice.
      event.preventDefault()
      dispatch(action)
    },
    [dispatch, getState, grid, isPositionRendered],
  )

  /*
    Focus landed inside the board on something that is not one of its positions: a button
    in a column header, one beside a swimlane's name, or an "Add card" in the band of a
    cell. Nothing else says so. The blur below fires only when focus leaves the board
    altogether, and no position reports arriving because none of them was focused, so
    without this the board would go on telling its caller the reader is on the card they
    have left.

    `focusin` bubbles, so an element at a position hears this same event first and says it
    took it, and both are the one batch of work React does for one event: a reader moving
    between two cards is never reported as being on neither.

    Asked of the position rather than worked out from the element focus landed on. Keeping
    the set of position elements and testing containment against it reads the wrong way
    round twice over: the board would have to know an element is a position before it has
    said so, which is untrue during the very commit that mounts a card whose own content
    takes focus, and containment is the wrong test for a cell body, which is a position
    whose descendants are not.
  */
  const onFocus = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (isFocusAtPosition(event.nativeEvent)) return

      dispatch({type: 'stray'})
    },
    [dispatch, isFocusAtPosition],
  )

  // The position is kept when focus leaves, so tabbing back in returns to where the
  // reader was. What is dropped is any outstanding request to land focus: somebody who
  // moved focus out of the board is not asking to be pulled back into it.
  const onBlur = useCallback(
    (event: FocusEvent<HTMLElement>) => {
      if (event.currentTarget.contains(event.relatedTarget)) return

      dispatch({type: 'release'})
    },
    [dispatch],
  )

  return {
    rootProps: {
      'aria-label': labels.board,
      tabIndex: isPositionRendered ? -1 : 0,
      onKeyDown,
      onFocus,
      onBlur,
      ref: setRef,
      // Published so the styled layer, and anyone assembling their own, can keep scroll
      // padding and sticky chrome clear of a scrollbar only some platforms draw. Safe
      // to set from a measurement of this same element: what reads them is scroll
      // padding, which changes where a scroll lands and never what anything is sized
      // to, so there is no loop back into the measurement.
      style: {
        '--board-scrollbar-block-size': `${scrollbarSizes.horizontal}px`,
        '--board-scrollbar-inline-size': `${scrollbarSizes.vertical}px`,
      },
    },
    scrollRef,
    scrollbarSizes,
    hasUnboundedHeight,
    hasRowHeaders,
  }
}

/**
 * Whether the board reads right to left, so that the key crossing it means the column
 * the reader sees in that direction.
 *
 * Read from the element rather than from the document, because a board can be inside a
 * subtree that states its own direction. Computed per keypress, which is one style read
 * on a key that is already going to move focus and scroll the board.
 */
function isRightToLeft(element: HTMLElement): boolean {
  return element.ownerDocument.defaultView?.getComputedStyle(element).direction === 'rtl'
}
